import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormControl as MuiFormControl, FormHelperText as MuiFormHelperText, InputLabel as MuiInputLabel, ListSubheader as MuiListSubheader, MenuItem as MuiMenuItem, Select as MuiSelect, unstable_useId as useId, } from '@mui/material';
import clsx from 'clsx';
import { forwardRef, useCallback, useEffect, } from 'react';
import { layout } from '../../styles';
import { styled } from '../../utils';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { CircularProgress } from '../Progress';
import { WithEndDecorator, } from '../WithEndDecorator';
export const Select = forwardRef(function SelectFunction(props, ref) {
    const { onChange, options = [], emptyOptionLabel, label, helperText, FormControlProps, required, value, disabled, endDecorator, emptyEndDecorator, loading, id: idOverride, grouping, autoSetIfOneElement = true, ...otherProps } = props;
    const handleChange = useCallback((event) => {
        const idx = event.target.value;
        const value = idx === '' ? null : options[idx].value;
        onChange(value);
    }, [onChange, options]);
    const idx = options.findIndex((option) => isObjectType(option.value) && isObjectType(value)
        ? option.value.id === value.id
        : option.value === value);
    const selectValue = idx === -1 ? '' : idx;
    useEffect(() => {
        // Select value automatically if there is only one option and it has not been selected yet
        if (autoSetIfOneElement) {
            if (options.length === 1 && selectValue !== 0) {
                onChange(options[0].value);
            }
        }
    }, [options, selectValue, onChange, autoSetIfOneElement]);
    const hasValue = selectValue !== '';
    const isDisabled = (autoSetIfOneElement && options.length < 2) || disabled || loading;
    // Set automatic id
    const id = useId(idOverride);
    const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
    const inputLabelId = label && id ? `${id}-label` : undefined;
    return (_jsx(WithEndDecorator, { endDecorator: endDecorator, emptyEndDecorator: emptyEndDecorator, decoratorComponent: _jsxs(FormControlStyled, { disabled: isDisabled /* || loading */, 
            // TODO: simplify code for component with/without end decorator
            className: clsx(!endDecorator && !emptyEndDecorator ? props.className : '', FormControlProps?.className), ...FormControlProps, children: [!!label && (_jsx(InputLabelStyled, { id: inputLabelId, error: props.error, children: label + (required ? '\xa0*' : '') })), _jsxs(SelectStyled, { innerRef: ref, required: required, value: selectValue, onChange: handleChange, input: _jsx(Input, { hasValue: hasValue }), IconComponent: loading
                        ? LoadingProgress
                        : (options.length < 2 && EmptyComponent) || ArrowIcon, labelId: inputLabelId, "aria-describedby": helperTextId, ...otherProps, children: [!required && !!emptyOptionLabel && (_jsx(MuiMenuItem, { value: "", children: _jsx("em", { children: emptyOptionLabel }) })), options.flatMap((option, idx) => [
                            _jsx(MuiMenuItem, { sx: { whiteSpace: 'unset', wordBreak: 'break-word' }, "data-name": option.dataName ? option.dataName : null, value: idx, disabled: option.disabled, children: option.label }, `${idx}${isObjectType(option.value)
                                ? (option.value.id?.toString() ?? '')
                                : option.value.toString()}`),
                            _jsx(MuiListSubheader, { children: grouping && grouping?.index === idx && grouping?.groupingName }, idx),
                        ])] }), !!helperText && (_jsx(FormHelperTextStyled, { error: props.error, id: helperTextId, children: helperText }))] }) }));
});
function isObjectType(value) {
    return (typeof value === 'object' && value !== null && Object.hasOwn(value, 'id'));
}
const FormControlStyled = styled(MuiFormControl)(() => ({
    width: '100%',
}));
const SelectStyled = styled(({ className, innerRef, ...props }) => (_jsx(MuiSelect, { ...props, ref: innerRef, MenuProps: {
        classes: { paper: `${className}-paper`, list: `${className}-list` },
        ...props.MenuProps,
    } })))(({ theme }) => ({
    '&-paper': {
        boxShadow: `${layout.shadow.s} !important`,
        borderRadius: `${theme.spacing(0.5)} !important`,
    },
    '&-list': {
        '& .MuiListItem-button:hover': {
            backgroundColor: theme.palette.secondary.contrastText,
        },
        '& .Mui-selected, & .Mui-selected:hover': {
            backgroundColor: theme.palette.groupPrimary.lightDove,
        },
    },
}));
const EmptyComponent = () => null;
const LoadingProgress = () => _jsx(CircularProgress, { filled: true, size: 20 });
const ArrowIcon = () => {
    const IconStyled = styled(Icon)(({ theme }) => ({
        top: 'calc(50% - 4px)',
        fontSize: theme.typography.body2.fontSize,
        color: 'rgba(0, 0, 0, 0.54)',
        right: 0,
        position: 'absolute',
        pointerEvents: 'none',
    }));
    return _jsx(IconStyled, { variant: "arrow-down-2", fontSize: "small" });
};
const InputLabelStyled = styled(({ className, ...props }) => (_jsx(MuiInputLabel, { ...props, classes: {
        root: `${className}-root`,
        shrink: `${className}-shrink`,
        animated: `${className}-animated`,
    } })))(({ theme }) => ({
    '&-root.MuiInputLabel-root': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        lineHeight: 1,
        paddingRight: theme.spacing(3),
        transform: `translate(0, ${theme.spacing(2.75)}) scale(1)`,
        '&:not(.Mui-error).Mui-focused': {
            color: theme.palette.primary.main,
        },
        '&.Mui-disabled': {
            color: theme.palette.white.dark,
        },
    },
    '&-shrink': {
        maxWidth: '133% !important',
        transform: 'translate(0, 1.5px) scale(0.75) !important',
    },
    '&-animated': {
        transition: `${theme.transitions.create(['color', 'transform', 'max-width'], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeOut,
        })} !important`,
    },
}));
const FormHelperTextStyled = styled(MuiFormHelperText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0, 0, 0),
}));
